import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase"; // Assuming you might need supabase later
import { Project, PreviewData } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlusCircle } from "lucide-react";
import { getProjectPreviews, getProjectById } from "@/services/project-service"; // Assuming getProjectById exists or will be added
import { PreviewCard } from "@/components/preview/preview-card";
import { PreviewCardSkeleton } from "@/components/preview/preview-card-skeleton";
import { BUCKET_NAME } from "@/lib/constants";
import { useTranslation } from "react-i18next";

// Placeholder for getProjectById if not already in project-service.ts
// You might need to add this to your project-service.ts
// export const getProjectById = async (projectId: string): Promise<Project | null> => {
//   try {
//     const { data, error } = await supabase
//       .from('projects')
//       .select('*')
//       .eq('id', projectId)
//       .single();
//     if (error) throw error;
//     return data;
//   } catch (error) {
//     console.error("Error fetching project by ID:", error);
//     return null;
//   }
// };


export default function ProjectDetailPage() {
  const { t, i18n } = useTranslation("common");
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [previews, setPreviews] = useState<PreviewData[]>([]);
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const [isLoadingPreviews, setIsLoadingPreviews] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;

    const fetchProjectDetails = async () => {
      setIsLoadingProject(true);
      // Assuming getProjectById is implemented in your service
      // For now, we'll just log or you can implement it.
      // const projectData = await getProjectById(projectId);
      // For demonstration, let's simulate fetching project data or assume it's passed via state/context
      // If you have getProjectById in your service, use it here.
      // For now, we'll focus on previews.
      // setProject(projectData);
      // setIsLoadingProject(false);

      // Fetch previews for this project
      setIsLoadingPreviews(true);
      try {
        const projectData = await getProjectById(projectId); // You'll need to implement this
        setProject(projectData);
        setIsLoadingProject(false);

        const previewsData = await getProjectPreviews(projectId);
         const previewsWithPublicUrls = previewsData?.map((preview) => {
          if (preview.asset_path) {
            const { data: { publicUrl } } = supabase.storage.from(BUCKET_NAME).getPublicUrl(preview.asset_path);
            return { ...preview, asset_path: publicUrl };
          }
          return preview;
        });
        setPreviews(previewsWithPublicUrls || []);
      } catch (err) {
        console.error("Error fetching project details or previews:", err);
        setError("Failed to load project details or previews.");
      } finally {
        setIsLoadingPreviews(false);
      }
    };

    fetchProjectDetails();
  }, [projectId]);

  const handlePreviewDelete = (id: string) => {
    setPreviews((prevPreviews) => prevPreviews.filter((preview) => preview.id !== id));
  };

  const handlePreviewEdit = (updatedPreview: PreviewData) => {
    setPreviews((prevPreviews) =>
      prevPreviews.map((preview) => (preview.id === updatedPreview.id ? updatedPreview : preview)),
    );
  };


  if (isLoadingProject) {
    return (
      <div className="container mx-auto px-4 py-6 text-center">
        {t("common:projectDetail.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6 text-center text-red-500">
        {t("common:projectDetail.error")}
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-6 text-center">
        {t("common:projectDetail.notFound")}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 container mx-auto px-4 py-6">
        <Button asChild variant="outline" size="sm" className="mb-6">
          <Link to="/projects">
            <ArrowLeft
              className={`mr-2 h-4 w-4 ${
                i18n.language === "ar" ? "rotate-180" : ""
              }`}
            />
            {t("common:projectDetail.backToProjects")}
          </Link>
        </Button>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">{project.name}</h1>
            <p className="text-muted-foreground">{project.description}</p>
          </div>
          <Button asChild>
            <Link to="/models">
              <PlusCircle className="mr-2 h-4 w-4" />
              {t("common:projectDetail.createPreview")}
            </Link>
          </Button>
        </div>

        <h2 className="text-2xl font-semibold mb-4">
          {t("common:projectDetail.previewsTitle")}
        </h2>

        {isLoadingPreviews && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(3).fill(0).map((_, i) => <PreviewCardSkeleton key={`loading-preview-${i}`} />)}
          </div>
        )}

        {!isLoadingPreviews && previews.length === 0 && (
          <div className="bg-muted/50 rounded-lg p-8 text-center">
            <p className="mb-4">{t("common:projectDetail.noPreviews")}</p>
            <Button asChild>
              <Link to="/models">
                <PlusCircle className="mr-2 h-4 w-4" />
                {t("common:projectDetail.createFirstPreview")}
              </Link>
            </Button>
          </div>
        )}

        {!isLoadingPreviews && previews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {previews.map((preview) => (
              <PreviewCard
                key={preview.id}
                preview={preview}
                onDelete={() => handlePreviewDelete(preview.id)}
                onEdit={handlePreviewEdit}
                showProjectTag={false} // Hide project tags in project detail view
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
